import React, { useCallback, useMemo, useState } from "react";
import { PostFieldLabel, PlatformUsage } from "components/Posts/PostForm";
import { FormControl } from "ui";
import { FIELDS, GMB_TOPICS } from "constants/PostFormFields";
import DateTimePicker from "components/DateTimePicker/DateTimePicker";
import { format, addDays, endOfDay, isBefore } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import PostFieldError from "components/Posts/PostForm/PostFieldError/PostFieldError";
import { useSelector } from "react-redux";
import { getUser } from "selectors/commonSelectors";

const PostEventDates = ({
  topicType,
  post,
  selectedPlatform,
  platforms,
  disabled = false,
  onChange,
  className = "",
  errors,
}) => {
  const forTopics = [GMB_TOPICS.OFFER.VALUE, GMB_TOPICS.EVENT.VALUE];
  if (!forTopics.includes(topicType)) {
    return "";
  }

  const user = useSelector(getUser);

  const { gmbEvent } = post;
  const [startDate, setStartDate] = useState(
    gmbEvent && gmbEvent.startDate
      ? toZonedTime(new Date(gmbEvent.startDate), user.timezone)
      : null
  );
  const [endDate, setEndDate] = useState(
    gmbEvent && gmbEvent.endDate
      ? toZonedTime(new Date(gmbEvent.endDate), user.timezone)
      : null
  );
  const now = new Date();
  const startInThePast = startDate ? isBefore(startDate, now) : false;
  const endInThePast = endDate ? isBefore(endDate, now) : false;

  return (
    <div className={className}>
      <div className="flex items-center">
        <PostFieldLabel className="mb-2 mr-4">
          {topicType === GMB_TOPICS.OFFER.VALUE ? "Offer" : "Event"} dates
          (required)
        </PostFieldLabel>
        <PlatformUsage
          platforms={platforms}
          field={FIELDS.GMB_START_DATE}
          selectedPlatform={selectedPlatform}
        />
      </div>
      <div className="flex">
        <EventDate
          className="mb-4 mr-4"
          date={startDate}
          onChange={(newDate) => {
            setStartDate(toZonedTime(new Date(newDate), user.timezone));
            onChange({ [FIELDS.GMB_START_DATE]: newDate });
          }}
          inThePast={startInThePast}
          field={FIELDS.GMB_START_DATE}
          disabled={disabled}
        >
          Start date
        </EventDate>
        <EventDate
          className=""
          date={endDate}
          onChange={(newDate) => {
            setEndDate(toZonedTime(new Date(newDate), user.timezone));
            onChange({ [FIELDS.GMB_END_DATE]: newDate });
          }}
          inThePast={endInThePast}
          field={FIELDS.GMB_END_DATE}
          disabled={disabled}
        >
          End date
        </EventDate>
      </div>
      <PostFieldError
        className="mt-4"
        fields={[FIELDS.GMB_START_DATE, FIELDS.GMB_END_DATE]}
        errors={errors}
      />
    </div>
  );
};

const EventDate = ({
  date,
  field,
  children,
  platforms,
  selectedPlatform,
  onChange,
  inThePast,
  className,
}) => {
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const scheduledClasses = "border-blue-500 bg-blue-200 text-blue-500";
  const scheduledErrorClasses = "border-red-500 bg-red-100 text-red-500";

  const timePreference = "12h";
  // const dateFormat = "d MMM hh:mma";
  const dateFormat = timePreference === "12h" ? "d MMM hh:mma" : "d MMM HH:mm";
  const disabled = false;
  const maxSelectableDate = endOfDay(addDays(new Date(), 364));

  return (
    <div className={className}>
      <DisplayBox
        className={`relative p-4 ${
          inThePast ? scheduledErrorClasses : scheduledClasses
        }`}
      >
        {children}:
        <span
          className="underline cursor-pointer ml-4"
          onClick={() => !disabled && setDatePickerVisible(!datePickerVisible)}
        >
          {date ? format(date, dateFormat) : "Pick date"}
        </span>
        {datePickerVisible && (
          <div
            className="absolute"
            style={{ transform: "translate(10%, -70%)", zIndex: 1000 }}
          >
            <DateTimePicker
              selectedDate={date}
              onChange={(newDateTime) => {
                setDatePickerVisible(false);
                onChange(newDateTime);
              }}
              timePreference={timePreference}
              onClose={() => setDatePickerVisible(false)}
              arrowSide="left"
              maxSelectableDate={maxSelectableDate}
            />
          </div>
        )}
      </DisplayBox>
    </div>
  );
};

const DisplayBox = ({ children, className = "" }) => {
  return (
    <div
      className={`flex items-center border border-solid rounded-lg font-medium px-4 ${className}`}
    >
      {children}
    </div>
  );
};

export default PostEventDates;
